import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import logo from "@/assets/logo.webp";
import DevAvatarAnimation from "@/components/auth/DevAvatarAnimation";

const ATTENDANCE_ALLOWED_ROLES = [
  "superadmin", "principal", "deputy_principal", "head_administration",
  "head_academic", "it_incharge", "security_man",
  "cr_cmt4", "cr_cmt5", "cr_cmt6", "cr_pst4", "cr_pst5", "cr_pst6",
  "cr_sw4", "cr_sw5", "cr_sw6",
];

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signIn(email, password);

      // Preflight role check: fetch fresh roles for this user and report
      // whether /attendance is accessible right after login.
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: roleData } = await supabase.rpc("get_user_roles", { _user_id: user.id });
        const userRoles = (roleData ?? []) as string[];
        const canAccessAttendance = userRoles.some((r) => ATTENDANCE_ALLOWED_ROLES.includes(r));

        toast({
          title: canAccessAttendance ? "Signed in — Attendance access granted" : "Signed in",
          description: userRoles.length
            ? `Your roles: ${userRoles.join(", ")}${canAccessAttendance ? "" : " · /attendance is not available for these roles."}`
            : "No roles assigned to your account. Contact an administrator.",
          variant: userRoles.length === 0 ? "destructive" : "default",
        });
      }

      navigate("/dashboard");
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4 gap-4 sm:gap-6 pb-safe">
      {/* Header: Logo */}
      <div className="flex flex-col items-center gap-2">
        <div className="h-[80px] w-[80px] sm:h-[104px] sm:w-[104px] rounded-full bg-primary/10 flex items-center justify-center p-2">
          <img src={logo} alt="St. David College" width={88} height={88} className="h-full w-full object-contain" />
        </div>
        <h1 className="text-lg sm:text-xl font-bold text-foreground text-center">St. David College of Health Sciences</h1>
        <p className="text-xs sm:text-sm font-medium text-muted-foreground">Student Management System</p>
      </div>

      {/* Login Card */}
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-lg">Sign in to your account</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Please wait..." : "Sign In"}
            </Button>
            <p className="text-center text-sm">
              <button type="button" onClick={() => navigate("/forgot-password")} className="text-primary hover:underline text-sm font-medium">
                Forgot Password?
              </button>
            </p>
            <p className="text-center text-sm text-muted-foreground">
              Student?{" "}
              <button type="button" onClick={() => navigate("/student-signup")} className="text-primary hover:underline font-medium">
                Register Here
              </button>
            </p>
          </form>
          <div className="flex justify-center pt-4">
            <DevAvatarAnimation />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
